import { useEffect, useState, memo, Fragment } from 'react';
import Sparkles from './components/sparkles';

import { companies, links } from './data';
import classNames from './styles.module.css';
import { Box } from './components/Box';

const FADE_IN_INTERVAL_MS = 100;

export type IntroProps = {
  animate?: boolean;
  onNavigate: (id: string) => void;
};

const Intro = memo(function Intro({ animate = true, onNavigate }: IntroProps) {
  const [scrollY, setScrollY] = useState<number>(0);

  useEffect(() => {
    const scrollEvent = () => {
      setScrollY(window.scrollY * -0.3);
    };
    window.addEventListener('scroll', scrollEvent);

    return () => {
      window.removeEventListener('scroll', scrollEvent);
    };
  });

  return (
    <div className={classNames.container}>
      <div className={classNames.background} style={{ top: `${scrollY}px` }} aria-hidden="true">
        {animate && <Sparkles />}
      </div>
      <div className={classNames.foreground}>
        <div className={classNames.content}>
          <section className={classNames.copyPadding} aria-hidden="true"></section>

          <section className={classNames.copy}>
            <header>
              <h1 className={`text-5xl font-medium ${classNames.header1}`}>John-Paul Holt</h1>
              <h2 className={`text-2xl font-medium ${classNames.header2}`}>
                <span>Web Development</span>
                <span className={classNames.purpleSlash} aria-hidden="true">
                  ╱
                </span>
                <span>UX Engineering</span>
                <span className={classNames.cyanSlash} aria-hidden="true">
                  ╱
                </span>
                <span>Prototyping</span>
              </h2>
              <p className={classNames.responsiveMessage}>
                This website is optimised for a laptop or tablet device. It will also show more
                visuals on a phone in landscape mode.
              </p>
            </header>
            <nav className={classNames.navigation} aria-label="Portfolio navigation">
              <div className={classNames.grid} role="list" aria-label="Companies worked with">
                {companies.map((company, index) => (
                  <div key={`${index}-${company.id}`} role="listitem">
                    <Box
                      onNavigate={onNavigate}
                      company={company}
                      fadeInDelay={(index + 1) * FADE_IN_INTERVAL_MS}
                      isEven={index % 2 !== 0}
                    />
                  </div>
                ))}
              </div>
              <ul className={classNames.resourceLinks} aria-label="Additional resources">
                {links.map((link, index) => (
                  <Fragment key={`${index}-${link.id}`}>
                    {!!index && (
                      <span className={classNames.slash} aria-hidden="true">
                        ╱
                      </span>
                    )}
                    <li
                      key={`${index}-${link.id}`}
                      className={link.hideOnMobile ? classNames.hideOnMobile : ''}
                    >
                      <a
                        href={link.url}
                        target={link.newTab ? '_blank' : undefined}
                        rel={link.newTab ? 'noreferrer noopener' : undefined}
                      >
                        <span>{link.text}</span>
                      </a>
                    </li>
                  </Fragment>
                ))}
              </ul>
            </nav>
          </section>
        </div>
      </div>
    </div>
  );
});

export default Intro;
